import * as React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { SectionCard, EmptyState, ConfirmDialog } from './ui';
import { TRUE_COLORS } from '../theme';
import { Category } from '../types';

const Categories = () => {
  const axios = useAxios();

  const [categories, setCategories] = React.useState<Category[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [newName, setNewName] = React.useState('');
  const [editing, setEditing] = React.useState<{ id: number; name: string } | null>(null);
  const [toDelete, setToDelete] = React.useState<Category | null>(null);
  const [counts, setCounts] = React.useState<Record<number, number>>({});

  React.useEffect(() => {
    (async () => {
      const [c, p] = await Promise.allSettled([axios.get('api/categories/'), axios.get('api/products/')]);
      if (c.status === 'fulfilled') setCategories(c.value.data ?? []);
      if (p.status === 'fulfilled') {
        const tally: Record<number, number> = {};
        (p.value.data ?? []).forEach((prod: any) => (prod.category ?? []).forEach((cat: Category) => { tally[cat.id] = (tally[cat.id] ?? 0) + 1; }));
        setCounts(tally);
      }
      setLoading(false);
    })();
  }, [axios]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    const { data } = await axios.post('api/categories/', { name });
    setCategories(cs => [...cs, data].sort((a, b) => a.name.localeCompare(b.name)));
    setNewName('');
  };

  const saveEdit = async () => {
    if (!editing?.name.trim()) return;
    const { data } = await axios.put(`api/categories/${editing.id}/`, { name: editing.name.trim() });
    setCategories(cs => cs.map(c => (c.id === data.id ? data : c)));
    setEditing(null);
  };

  const sorted = [...categories].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <PageShell back={{ to: '/admin/products', label: 'Productos' }} title="Categorías" subtitle="Agrupa tus productos para encontrarlos y analizarlos más fácil">
      <Box sx={{ maxWidth: 720 }}>
        <SectionCard noPadding>
          <Box component="form" onSubmit={add} sx={{ p: 2.5, borderBottom: `1px solid ${TRUE_COLORS.borderSolid}` }}>
            <Stack direction="row" spacing={1.5}>
              <TextField
                fullWidth
                placeholder="Nueva categoría, p. ej. Bebidas"
                value={newName}
                onChange={e => setNewName(e.target.value)}
              />
              <Button type="submit" variant="contained" startIcon={<AddIcon />} disabled={!newName.trim()} sx={{ flexShrink: 0 }}>
                Agregar
              </Button>
            </Stack>
          </Box>
          {!loading && sorted.length === 0 ? (
            <EmptyState icon={<CategoryOutlinedIcon />} title="Aún no hay categorías" description="Crea categorías como Bebidas, Abarrotes o Limpieza." />
          ) : (
            <List disablePadding>
              {sorted.map(cat => (
                <ListItem
                  key={cat.id}
                  divider
                  sx={{ px: 2.5, py: 1 }}
                  secondaryAction={
                    editing?.id === cat.id ? (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Guardar"><IconButton onClick={saveEdit}><CheckIcon /></IconButton></Tooltip>
                        <Tooltip title="Cancelar"><IconButton onClick={() => setEditing(null)}><CloseIcon /></IconButton></Tooltip>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Renombrar"><IconButton onClick={() => setEditing({ id: cat.id, name: cat.name })}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton onClick={() => setToDelete(cat)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    )
                  }
                >
                  {editing?.id === cat.id ? (
                    <TextField
                      autoFocus
                      value={editing.name}
                      onChange={e => setEditing({ ...editing, name: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }}
                      sx={{ mr: 12, flexGrow: 1 }}
                    />
                  ) : (
                    <ListItemText
                      primary={cat.name}
                      secondary={`${counts[cat.id] ?? 0} ${(counts[cat.id] ?? 0) === 1 ? 'producto' : 'productos'}`}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                    />
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </SectionCard>
      </Box>

      <ConfirmDialog
        open={!!toDelete}
        danger
        title={`¿Eliminar "${toDelete?.name}"?`}
        description="Los productos de esta categoría no se eliminan; solo dejarán de estar agrupados."
        confirmLabel="Eliminar"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          await axios.delete(`api/categories/${toDelete.id}/`);
          setCategories(cs => cs.filter(c => c.id !== toDelete.id));
          setToDelete(null);
        }}
      />
    </PageShell>
  );
};

export default Categories;
